import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import {
  TimesheetInvoiceAngebotAlert,
  type ProjectAngebotHint,
} from "@/features/timesheetFlow/components/TimesheetInvoiceAngebotAlert";
import type { GdInvoiceCandidate } from "@/features/timesheetFlow/timesheetFlowApi";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";

function formatPeriod(from?: string | null, to?: string | null): string {
  const fmt = (value?: string | null) => {
    const raw = String(value || "").slice(0, 10);
    if (!raw || raw.length < 10) return "";
    const [y, m, d] = raw.split("-");
    return `${d}.${m}.${y}`;
  };
  const a = fmt(from);
  const b = fmt(to);
  if (a && b && a !== b) return `${a} – ${b}`;
  return a || b || "—";
}

function formatHours(value?: number | null): string {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "0 h";
  return `${n.toLocaleString("de-DE", { maximumFractionDigits: 2 })} h`;
}

export function TimesheetInvoiceMultiSelectDialog({
  open,
  busy = false,
  primary,
  candidates,
  angebote = [],
  onClose,
  onConfirm,
}: {
  open: boolean;
  busy?: boolean;
  primary: GdInvoiceCandidate | null;
  candidates: GdInvoiceCandidate[];
  angebote?: ProjectAngebotHint[];
  onClose: () => void;
  onConfirm: (timesheetIds: number[]) => void;
}) {
  const layers = useTimesheetFlowLayers();
  const items = useMemo(() => {
    if (!primary) return [];
    return [primary, ...candidates];
  }, [primary, candidates]);

  const [selected, setSelected] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!open || !primary) return;
    setSelected(new Set([primary.id, ...candidates.map((c) => c.id)]));
  }, [open, primary, candidates]);

  const toggle = (id: number) => {
    if (primary && id === primary.id) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(items.map((i) => i.id)));
  };

  const handleConfirm = () => {
    if (!primary) return;
    const ids = items.map((i) => i.id).filter((id) => selected.has(id));
    if (!ids.includes(primary.id)) ids.unshift(primary.id);
    onConfirm(ids);
  };

  const additionalCount = candidates.length;

  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      title="Weitere Stundennachweise abrechnen?"
      size="lg"
      zIndexClass={layers.deeper}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="button" variant="secondary" onClick={selectAll} disabled={busy || !items.length}>
            Alle auswählen
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={busy || selected.size === 0} loading={busy}>
            {selected.size > 1
              ? `${selected.size} Nachweise abrechnen`
              : "Nur diesen Nachweis abrechnen"}
          </Button>
        </div>
      }
    >
      {angebote.length ? <TimesheetInvoiceAngebotAlert angebote={angebote} className="mb-4" /> : null}

      <p className="mb-4 text-sm text-label-secondary">
        Für dieses Projekt gibt es noch{" "}
        <span className="font-medium text-label">
          {additionalCount === 1
            ? "einen weiteren unterschriebenen Stundennachweis"
            : `${additionalCount} weitere unterschriebene Stundennachweise`}
        </span>
        , die noch nicht abgerechnet sind. Wählen Sie aus, welche in dieselbe Rechnung
        übernommen werden sollen. Jeder Nachweis erhält einen eigenen Titelblock mit seinen
        Positionen.
      </p>

      <ul className="max-h-[50vh] space-y-1 overflow-y-auto">
        {items.map((item) => {
          const isPrimary = primary?.id === item.id;
          const checked = selected.has(item.id);
          return (
            <li key={item.id}>
              <label
                className={`flex cursor-pointer gap-3 rounded-ios px-3 py-2 ${
                  isPrimary ? "bg-accent-soft/40" : "hover:bg-fill"
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={checked}
                  disabled={busy || isPrimary}
                  onChange={() => toggle(item.id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-label">
                    Stundennachweis Nr. {item.display_number || item.id}
                    {isPrimary ? (
                      <span className="ml-2 text-xs font-normal text-accent">aktuell</span>
                    ) : null}
                  </span>
                  <span className="block text-xs text-label-tertiary">
                    {formatPeriod(item.date_from, item.date_to)} · {formatHours(item.total_hours)}
                  </span>
                  {item.work_description?.trim() ? (
                    <span className="mt-0.5 block truncate text-xs text-label-secondary">
                      {item.work_description.trim()}
                    </span>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </Dialog>
  );
}
